import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Button, Col, Dropdown, Form, Row } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import { SafeDatatable } from "../../components/SafeDatatable";
//import ReactDatatable from "@ashvin27/react-datatable";
import AuthService from "../../services/auth.service";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCogs, faEdit, faTrash, faLock } from "@fortawesome/free-solid-svg-icons";

import moment from 'moment';
import _ from 'lodash';

import { DatasetDefault, DatasetModel } from "../../models/dataset.model";
import { ProfileModel } from "../../models/profile.model";
import { UserModel } from "../../models/user.model";
import { RequestInputOutputParamsModel, RequestInputOutputParamsModelDefault } from "../../models/requestInputOutputParams.model";

import storageService from "../../services/storage.service";
import { ErrorHandler } from "../../services/errorHandler.service";
import { ToastService } from "../../services/toast.service";
import { ProfileService } from '../../services/profile.service';
import { UserService } from "../../services/user.service";
import { FileExportService } from "../../services/fileExport.service";

import { HeaderList } from "../../components/HeaderList";
import { CustomModal } from "../../components/Modal";
import DropdownButtonExport from "../../components/dropdownButton/export";

import { ButtonClean, ButtonSearch } from '../../components/buttons';
import UtilService from "../../services/util.service";

export const UserList = () => {
  const [profile, setProfile] = useState<ProfileModel[]>();
  const { pathname } = useLocation();
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation();
  const idiom = storageService.getIdiom();
  const [data, setData] = useState<DatasetModel>();
  const [showRemoveModal, setShowRemoveModal] = useState({
    id: 0,
    show: false,
    name: "",
    entity: null
  });
  const [showChangePassModal, setShowChangePassModal] = useState({
    email: "",
    show: false,
    name: "",
    entity: null
  });
  
  const history = useHistory();
  const [dataset, setDataset] = useState<DatasetModel>({
    ...JSON.parse(JSON.stringify(DatasetDefault)), dataset: 'user', filters: {
      email: [],
      fullname: [],
      is_active: undefined,
      is_blocked: undefined,
      profile_id: []
    },
    sort: {
      field: 'fullname',
      orientation: 'asc'
    }
  });
  const user = storageService.getUser();
  const dataTableConfigLocale = useMemo(() => UtilService.verifyIdiomDataTable(), [t]);

  const columns = [
    {
      key: "fullName",
      text: t("USER.FULLNAME"),
      align: "left",
      sortable: true,
    },
    {
      key: "email",
      text: t("USER.EMAIL"),
      align: "left",
      sortable: true,
    },
    {
      key: "profileName",
      text: t("USER.PROFILE"),
      align: "left",
      sortable: true,
      cell: (record) => {
        return t('PROFILE.' + record.profileName.toUpperCase());
      },
    },
    {
      key: "isActive",
      text: t("USER.ACTIVE"),
      align: "left",
      sortable: true,
      cell: (record) => {
        return record.isActive ? t("USER.ACTIVE") : t("USER.INACTIVE");
      },
    },
    {
      key: "updatedAt",
      text: t("USER.UPDATED_AT"),
      align: "left",
      sortable: true,
      cell: (record) => {
        return record.updatedAt ? moment(record.updatedAt).format(user.locale.formatDateTime) : '';
      },
    },
    {
      key: "action",
      text: t("ACTIONS"),
      className: "action",
      width: 100,
      align: "left",
      sortable: false,
      cell: (record: UserModel) => {
        return (
          <Fragment>
            <Dropdown className='btn-toolbar'>
              <Dropdown.Toggle
                as={Button}
                variant='primary'
                size='sm'
                className='me-2'>
                <FontAwesomeIcon icon={faCogs} className='me-2' />
              </Dropdown.Toggle>
              <Dropdown.Menu className='dashboard-dropdown dropdown-menu-left mt-2'>
                <Dropdown.Item
                  className='fw-bold'
                  onClick={() => history.push(`${pathname}/edit/${record.id}`)}>
                  <FontAwesomeIcon icon={faEdit} className='me-2' /> 
                  {(user.profile.profileName === "adm_reader" || user.profile.profileName === "sys_admin") ? (
                        t("VIEW")
                      ):
                        t("EDIT")
                  }
                </Dropdown.Item>
                {record.profileId !== 1 &&
                  <Dropdown.Item
                    className='fw-bold'
                    onClick={() => actionRemove(record.id, record.fullName, record)}
                    disabled={user.profile.profileName === "adm_reader" || user.profile.profileName === "sys_admin"}
                    >
                    <FontAwesomeIcon icon={faTrash} className='me-2' /> {t('DELETE')}
                  </Dropdown.Item>
                }
                {record.profileId !== 1 &&
                  <Dropdown.Item
                    className='fw-bold'
                    onClick={() => actionRecoverPassword(record.email, record.fullName, record)}
                    disabled={user.profile.profileName === "adm_reader" || user.profile.profileName === "sys_admin"}
                    >
                    <FontAwesomeIcon icon={faLock} className='me-2' /> {t('PASSWORD_RESET')}
                  </Dropdown.Item>
                }
              </Dropdown.Menu>
            </Dropdown>
          </Fragment>
        );
      },
    },
  ];

  const getList = async (dataTable,datasetOverride = null) => {
    setLoading(true);

    var requestParams: RequestInputOutputParamsModel = { ...RequestInputOutputParamsModelDefault, datasets: [datasetOverride ||dataset] };
   
    if (dataTable !== null && requestParams?.datasets) {
      let dataSet = requestParams.datasets[0];
      let pagination = dataSet.pagination;

      pagination.page_number = dataTable.page_number;
      pagination.records_pages = dataTable.page_size;
      dataSet.sort.field = dataTable.sort_order.column;
      dataSet.sort.orientation = dataTable.sort_order.order;
    } else {
      let pagination = requestParams.datasets[0].pagination;
      pagination.page_number = DatasetDefault.pagination.page_number;
      pagination.records_pages = DatasetDefault.pagination.records_pages;
    }

    let sort = requestParams.datasets[0].sort;
    if (!sort) {
        sort = requestParams.datasets[0].sort = {};
    }
    if (!sort.field || sort.field === null || sort.field.trim?.() === "") {
        sort.field = "fullname";
    }
    if (!sort.orientation || sort.orientation === null || sort.orientation.trim?.() === "") {
        sort.orientation = "asc";
    }  

    const response: any = await UserService.getList(requestParams);
    if (response.success === true && response.result) {
      let d: DatasetModel = _.last(_.filter(response.result.datasets, { dataset: 'user' }));
      setData(d);
    } else {
      requestParams.datasets[0].data = null;
      setData(requestParams.datasets[0]);
    }

    setLoading(false);
  };

  const handleSearch = async (event) => {
    if (event != null) {
      event.preventDefault();
    }


    const trimemail = dataset.filters.email?.[0]?.trim() || "";
    const trimfullname = dataset.filters.fullname?.[0]?.trim() || "";

    const cleanedDataset = {
      ...dataset,
      filters: {
        ...dataset.filters,
        email: [trimemail],
        fullname: [trimfullname],
      },
    };

    await getList(null,cleanedDataset);
  };

  const tableChangeHandler = async (data) => {
    await getList(data);
  };

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  }

  const [flagClear, setFlagClear] = useState(false);

  useEffect(() => {
        if (flagClear)
        {
            handleSearch(null);
            setFlagClear(false);
        }
      }, [flagClear]);

  const query = useQuery();

  const handleClean = (event) => {
    if (event != null) {
      event.preventDefault();
    }

    setDataset({
      ...dataset, filters: {
        email: [],
        fullname: [],
        is_active: undefined,
        is_blocked: undefined,
        profile_id: []
      },
        sort: {
        field: "",
        orientation: "",
      },
    });

    setFlagClear(true);
  };

  const actionRemove = (id, name, entity) => {
    setShowRemoveModal({ id: id, show: true, name: name, entity });
  };

  const actionRecoverPassword = (email, name, entity) => {
    setShowChangePassModal({ email: email, show: true, name: name, entity });
  };

  const handleCloseModal = () =>
    setShowRemoveModal({ id: 0, show: false, name: "", entity: null });

  const handleCloseModalPass = () =>
    setShowChangePassModal({ email: "", show: false, name: "", entity: null });

  const remove = async () => {
    if (showRemoveModal.id) {
      try {
        const response: any = await UserService.del(showRemoveModal.entity);
        if (ErrorHandler.success(response)) {
          if (response.success) {
            ToastService.success(t('USER.REMOVED'));
            handleCloseModal();
            handleClean(null);
            await getList(null);
          }
        }
      } catch (error) { }
    }
  };

  const changePass = async () => {
    setLoading(true);
    AuthService
            .forgotPassword(showChangePassModal.email)
            .then((response) => {
                if (response.success) {
                    ToastService.success(t('USER.SUCCESS_SEND_RESET_PASSWORD'));
                }else{
                  ToastService.success(t('ERROR_GENERIC'));
                }
            }).catch((e: any) => {
                ToastService.error(t('ERROR_GENERIC'));
            }).finally(() => {
                setLoading(false);
                handleCloseModalPass();
            });
  }

  const initialize = async () => {
    await getList(null);

    ProfileService.getAll().then((response: any) => {
      const sort = response.result.sort((a, b) => a.profileName.localeCompare(b.profileName));
      setProfile(sort);
    });
  };

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeaderList path={`${pathname}/add`} title={t("USER.TITLE_LIST")} disabled={user.profile.profileName === "adm_reader" || user.profile.profileName === "sys_admin"} />

      <CustomModal
        Title={t("DELETE")}
        Message={`${t('USER.REMOVE_ANSWER')} ${showRemoveModal ? showRemoveModal.name : ""}?`}
        close={handleCloseModal}
        cancel={t('CANCEL')}
        show={showRemoveModal ? showRemoveModal.show : false}>

        <Button variant='primary' onClick={remove}>
          {t('YES')}
        </Button>
      </CustomModal>

      <CustomModal
        Title={t("PASSWORD_CHANGE")}
        Message={`${t('USER.SEND_PASSWORD_CHANGE')} ${showChangePassModal ? showChangePassModal.name : ""}?`}
        close={handleCloseModalPass}
        cancel={t('CANCEL')}
        show={showChangePassModal ? showChangePassModal.show : false}>

        <Button variant='primary' onClick={changePass}>
          {t('YES')}
        </Button>
      </CustomModal>

      <Form>
        <Form.Label>{t("FILTERS")}</Form.Label>
        <Row>

          <Col xs="3" style={{padding: '0px 4px'}}>
            <Form.Group>
              <Form.Floating>
                <Form.Control
                  name='fullname'
                  id='fullname'
                  type='text'
                  placeholder={t('FILTER.FULLNAME')}
                  className='form-control'
                  onChange={(e) => {


                    setDataset({ ...dataset, filters: { ...dataset.filters, fullname: [e.target.value] } })
                  }}
                  value={dataset?.filters?.fullname.length > 0 ? dataset.filters.fullname[0] : ''}
                />
                <label htmlFor="fullname">{t("FILTER.FULLNAME")}</label>
              </Form.Floating>
            </Form.Group>
          </Col>

          <Col xs="2" style={{padding: '0px 4px'}}>
            <Form.Group>
              <Form.Floating>
                <Form.Control
                  name='email'
                  id='email'
                  type='email'
                  placeholder={t("FILTER.EMAIL")}
                  className='form-control'
                  onChange={(e) => {
                    setDataset({ ...dataset, filters: { ...dataset.filters, email: [e.target.value] } })
                  }}
                  value={dataset?.filters?.email.length > 0 ? dataset.filters.email[0] : ''}
                />
                <label htmlFor="email">{t("FILTER.EMAIL")}</label>
              </Form.Floating>
            </Form.Group>
          </Col>

          <Col xs="2" style={{padding: '0px 4px'}}>
            <Form.Group>
              <Form.Floating>
                <Form.Control
                  as='select'
                  className='form-control'
                  value={dataset?.filters?.profile_id.length > 0 ? dataset.filters.profile_id[0] : ''}
                  onChange={(e) => {

                    if(e.target.value == ''){
                        setDataset({ ...dataset, filters: { ...dataset.filters, profile_id: [] } })
                    }
                    else{
                      setDataset({ ...dataset, filters: { ...dataset.filters, profile_id: [Number(e.target.value)] } })
                    }
                  }}
                >
                  <option value=''>{t("FILTER.PROFILE")}</option>
                  {_.map(profile, (l: ProfileModel) => {
                    return <option key={l.id} value={l.id}>{t(`PROFILE.${l.profileName.toUpperCase()}`)}</option>
                  })}
                </Form.Control>
                <label htmlFor="status">{t("FILTER.PROFILE")}</label>
              </Form.Floating>
            </Form.Group>
          </Col>

          <Col xs="1" style={{padding: '0px 4px'}}>
            <Form.Group>
              <Form.Floating>
                <Form.Control
                  as='select'
                  className='form-control'
                  value={dataset?.filters?.is_active !== undefined ? dataset?.filters?.is_active : ''}
                  onChange={(e) => {

                  if(e.target.value == ''){
                     setDataset({ ...dataset, filters: { ...dataset.filters, is_active: undefined } })
                  }
                  else{
                    setDataset({ ...dataset, filters: { ...dataset.filters, is_active: (e.target.value ? e.target.value === 'true' : '') } })
                  }
                }}
                >
                  <option value=''>Status</option>
                  <option value='true'>Ativo</option>
                  <option value='false'>Inativo</option>
                </Form.Control>
                <label htmlFor="status">{t("FILTER.STATUS")}</label>
              </Form.Floating>
            </Form.Group>
          </Col>

          <Col xs="auto" style={{padding: '0px 4px'}}>

            <ButtonSearch onClick={(e) => handleSearch(e)} />

          </Col>

          <Col xs="auto" style={{padding: '0px 4px'}}>

            <ButtonClean onClick={async (e) => handleClean(e)} />

          </Col>

          <Col xs="auto" style={{padding: '0px 4px'}}>

            <DropdownButtonExport onClick={(type: string) =>
              FileExportService.Export(
                { ...RequestInputOutputParamsModelDefault, datasets: [dataset] },
                "User",
                type
              )}
            />

          </Col>

        </Row>
      </Form>
      <SafeDatatable
        key={flagClear ? "reset" : JSON.stringify(dataset.filters)}
        config={dataTableConfigLocale}
        records={data?.data ? data?.data : []}
        columns={columns}
        dynamic={true}
        total_record={data?.pagination?.total_records ? data?.pagination?.total_records : 0}
        onChange={tableChangeHandler}
        className='table table-hover user-table align-items-center'
      />
    </>
  );
};